import { useState } from 'react'

interface DonutChartProps {
  data: Array<{
    label: string
    value: number
    color: string
    percentage: string
  }>
}

export function DonutChart({ data }: DonutChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  // Calculate total for percentages
  const total = data.reduce((sum, item) => sum + item.value, 0)

  // Get the segment being hovered or the first one for default display
  const displayedSegment = hoveredIndex !== null ? data[hoveredIndex] : (data[0] || null)

  return (
    <div className='flex items-center gap-6'>
      {/* Chart */}
      <div className='relative w-[165px] h-[165px]'>
        <svg viewBox='-1 -1 2 2' className='transform -rotate-90 w-full h-full'>
          {data.map((slice, index) => {
            // Calculate cumulative percentages for SVG paths
            const cumulativePercent = data
              .slice(0, index)
              .reduce((sum, item) => sum + item.value / total, 0)

            const getCoordinatesForPercent = (percent: number) => {
              const x = Math.cos(2 * Math.PI * percent)
              const y = Math.sin(2 * Math.PI * percent)
              return [x, y]
            }

            const [startX, startY] = getCoordinatesForPercent(cumulativePercent)
            const nextPercent = cumulativePercent + slice.value / total
            const [endX, endY] = getCoordinatesForPercent(nextPercent)
            const largeArcFlag = slice.value / total > 0.5 ? 1 : 0

            const pathData = [
              `M ${(startX ?? 0) * 0.9} ${(startY ?? 0) * 0.9}`,
              `A 0.9 0.9 0 ${largeArcFlag} 1 ${(endX ?? 0) * 0.9} ${(endY ?? 0) * 0.9}`,
              `L ${(endX ?? 0) * 0.55} ${(endY ?? 0) * 0.55}`,
              `A 0.55 0.55 0 ${largeArcFlag} 0 ${(startX ?? 0) * 0.55} ${(startY ?? 0) * 0.55}`,
              'Z',
            ].join(' ')

            return (
              <path
                key={index}
                d={pathData}
                fill={slice.color}
                className='cursor-pointer transition-opacity hover:opacity-80'
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            )
          })}
        </svg>

        {/* Center Tooltip */}
        {displayedSegment && (
          <div
            className='absolute bg-white rounded-md shadow-lg px-2 py-1 pointer-events-none z-10'
            style={{
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              boxShadow: '0px 6px 16px 0px rgba(20, 23, 31, 0.15)',
            }}
          >
            <div className='flex flex-col items-start'>
              <span className='font-semibold text-[#14171f] text-[12px] leading-6'>{displayedSegment.percentage}</span>
              <span className='font-medium text-black text-[9px] leading-4 -mt-1'>{displayedSegment.label}</span>
            </div>
            {/* Left arrow pointer */}
            <div
              className='absolute left-[-8px] top-1/2 -translate-y-1/2'
              style={{
                width: 0,
                height: 0,
                borderTop: '6px solid transparent',
                borderBottom: '6px solid transparent',
                borderRight: '8px solid white',
              }}
            />
          </div>
        )}
      </div>

      {/* Legend */}
      <div className='flex flex-col gap-4'>
        {data.map((item, index) => (
          <div
            key={index}
            className='flex items-center gap-1 cursor-pointer'
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <div
              className='w-[10px] h-[10px] rounded-full'
              style={{ backgroundColor: item.color }}
            />
            <span className='text-[14px] text-[#414651] font-normal leading-5'>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
